import { CampusSelect } from '@/components/common/input/campus';
import type { RoleKey } from '@/store/users/types';
import type { User } from '@/shared/types/user';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  PhoneInput,
} from '@/components/ui';
import { useEffect, useState } from 'react';
import { PeopleAutocomplete } from '@/components/common/input/people';
import type { Person } from '@/store/people/types';
import { getFullName } from '@/shared/lib/names';
import { PersonItem } from '@/components/common/output/person';

export type UserSavePayload =
  | { user: User; mode: 'create'; invite: boolean }
  | { user: User; mode: 'update' };

export type EditMode = 'create' | 'update';
export type UserSource = 'new-user' | 'from-profile';

export function UserEdit({
  item,
  isOpen,
  onOpenChange,
  onClose: onDialogClose,
  onSave: saveHandler,
  mode,
  source,
  saving,
  errorMessage,
}: {
  item: User | null;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onClose?: () => void;
  onSave?: (payload: UserSavePayload) => void;
  mode: EditMode;
  source: UserSource;
  saving: boolean;
  errorMessage?: string;
}) {
  const [step, setStep] = useState<0 | 1 | 2>();
  useEffect(() => setStep(source === 'from-profile' ? 1 : 2), [source]);

  const [profile, setProfile] = useState<Person | null>(null);
  const onContinue = () => {
    if (!profile) {
      return;
    }

    setEditable({
      id: '',
      data: {
        displayName: getFullName(profile.data),
        email: profile.data.email || '',
        phone: profile.data.phone || '',
        profile_id: profile.id,
        campus_id: profile.data.campus_id || '',
        uid: profile.data.uid || '',
      },
    } as User);
    setStep(2);
  };

  const [editable, setEditable] = useState<User | null>(null);
  const [invite, setInvite] = useState(false);
  useEffect(() => {
    setEditable(item);
  }, [item]);
  const onAccessChange = (key: RoleKey, isSelected: boolean) => {
    if (!editable) {
      return;
    }

    setEditable({
      id: editable.id,
      data: {
        ...editable.data,
        [key]: isSelected,
      },
    });
  };

  const onCampusChange = (id: string) => {
    if (!editable) {
      return;
    }

    setEditable({
      id: editable.id,
      data: {
        ...editable.data,
        campus_id: id,
      },
    });
  };

  const onDisplayNameChange = (value: string) => {
    if (!editable) {
      return;
    }

    value = value.trim().replace(/ +/g, ' ');
    setEditable({
      id: editable.id,
      data: {
        ...editable.data,
        displayName: value,
      },
    });
  };

  const onEmailChange = (value: string) => {
    if (!editable) {
      return;
    }

    value = value.trim().toLowerCase();
    setEditable({
      id: editable.id,
      data: {
        ...editable.data,
        email: value,
      },
    });
  };

  const onPhoneChange = (value: string) => {
    if (!editable) {
      return;
    }

    value = value.trim();
    setEditable({
      id: editable.id,
      data: {
        ...editable.data,
        phone: value,
      },
    });
  };

  useEffect(() => {
    if (isOpen) {
      return;
    }

    setEditable(null);
    setProfile(null);
  }, [isOpen]);
  const onSave = () => {
    if (!editable) {
      return;
    }

    if (mode === 'create') {
      saveHandler?.({ user: editable, mode, invite });
    } else {
      saveHandler?.({ user: editable, mode });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onClose={onDialogClose}
      scrollBehavior='inside'
    >
      <ModalContent>
        {(onClose) =>
          editable ? (
            <>
              <ModalHeader>
                <div className='flex flex-col gap-1'>
                  <span>{mode === 'create' ? 'New' : 'Edit'} User</span>
                  <span className='text-sm font-normal text-foreground-600'>
                    Update user information and system access.
                  </span>
                </div>
              </ModalHeader>
              <ModalBody>
                {step === 1 && <LinkStep setSelected={setProfile} />}
                {step === 2 && (
                  <EditStep
                    {...{
                      editable,
                      profile,
                      mode,
                      onAccessChange,
                      onCampusChange,
                      onDisplayNameChange,
                      onEmailChange,
                      onPhoneChange,
                      setInvite,
                    }}
                  />
                )}
                {errorMessage && <Alert color='danger' title={errorMessage} />}
              </ModalBody>
              <ModalFooter>
                {step === 1 && (
                  <LinkFooter selected={profile} onContinue={onContinue} />
                )}
                {step === 2 && (
                  <EditFooter {...{ mode, saving, onSave, onClose }} />
                )}
              </ModalFooter>
            </>
          ) : (
            <></>
          )
        }
      </ModalContent>
    </Modal>
  );
}

function EditStep({
  mode,
  profile,
  editable,
  onDisplayNameChange,
  onEmailChange,
  onPhoneChange,
  onCampusChange,
  onAccessChange,
  setInvite,
}: {
  mode: EditMode;
  profile: Person | null;
  editable: User;
  onDisplayNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onCampusChange: (id: string) => void;
  onAccessChange: (key: RoleKey, value: boolean) => void;
  setInvite: (value: boolean) => void;
}) {
  return (
    <>
      {profile && <PersonItem item={profile} />}
      {(!profile || !getFullName(profile.data)) && (
        <Input
          label='Name'
          defaultValue={editable.data.displayName}
          autoComplete='off'
          onValueChange={onDisplayNameChange}
        />
      )}
      {!profile?.data.email && (
        <Input
          label='Email Address'
          defaultValue={editable.data.email}
          autoComplete='off'
          onValueChange={onEmailChange}
        />
      )}
      {!profile?.data.phone && (
        <PhoneInput
          label='Phone'
          value={editable.data.phone ?? ''}
          onChange={onPhoneChange}
          placeholder='(000) 000-0000'
        />
      )}
      <CampusSelect
        label='Campus'
        value={editable.data.campus_id || ''}
        onChangeSingle={onCampusChange}
      />
      <Card className='border-1 border-gray-200 px-3 pt-3' shadow='none'>
        <label className='mb-3 text-[75%] font-medium uppercase tracking-wide text-foreground-600'>
          System Access
        </label>
        {access.map((entry) => (
          <Checkbox
            className='mb-1'
            key={entry.key}
            defaultSelected={editable.data[entry.key]}
            size='sm'
            onValueChange={(isSelected: boolean) =>
              onAccessChange(entry.key, isSelected)
            }
          >
            {entry.label}
          </Checkbox>
        ))}
      </Card>
      {mode === 'create' && (
        <Checkbox onValueChange={setInvite}>Send invite</Checkbox>
      )}
    </>
  );
}

function EditFooter({
  onClose,
  onSave,
  saving,
  mode,
}: {
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  mode: EditMode;
}) {
  return (
    <>
      <Button onPress={onClose} disabled={saving}>
        Cancel
      </Button>
      <Button
        color='primary'
        variant='solid'
        onPress={onSave}
        isLoading={saving}
      >
        {mode === 'create' ? 'Create' : 'Update'}
      </Button>
    </>
  );
}

function LinkStep({
  setSelected,
}: {
  setSelected: (selected: Person | null) => void;
}) {
  const [person, setPerson] = useState<Person | null>(null);
  useEffect(() => {
    setSelected(person);
  }, [person, setSelected]);

  return (
    <>
      <PeopleAutocomplete label='Profile' onChange={setPerson} phone={false} />
      {/* <Debug value={person} /> */}
    </>
  );
}

function LinkFooter({
  selected,
  onContinue,
}: {
  selected: Person | null;
  onContinue: () => void;
}) {
  return (
    <>
      <Button
        variant='solid'
        color='primary'
        onPress={onContinue}
        isDisabled={!selected}
      >
        Continue
      </Button>
    </>
  );
}

const access: { key: RoleKey; label: string }[] = [
  { key: 'admin', label: 'Admin' },
  { key: 'people', label: 'People' },
  { key: 'workflows', label: 'Workflows' },
];
